'use client';

import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';
import { Megaphone, MessageSquare, Plus, Users, UserCog, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const audienceLabels: Record<string, { label: string; icon: typeof Users; color: string }> = {
  all_parents: { label: 'All Parents', icon: Users, color: 'bg-green-100 text-green-700' },
  all_coaches: { label: 'All Coaches', icon: UserCog, color: 'bg-blue-100 text-blue-700' },
  all_members: { label: 'Everyone', icon: Megaphone, color: 'bg-purple-100 text-purple-700' },
  session_attendees: { label: 'Session', icon: Calendar, color: 'bg-amber-100 text-amber-700' },
};

const priorityColors: Record<string, string> = {
  low: 'border-l-gray-300',
  normal: 'border-l-blue-400',
  high: 'border-l-amber-500',
  urgent: 'border-l-red-500',
};

export default function CoachAnnouncementsPage() {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    targetSessionId: '',
    priority: 'normal',
  });

  const announcementsData = useQuery(api.coach.announcements.list, { limit: 50 });
  const announcements = announcementsData?.announcements ?? [];
  const total = announcementsData?.pagination?.total;
  const isLoading = announcementsData === undefined;

  const today = new Date().toISOString().split('T')[0];
  const sessionsData = useQuery(api.coach.sessions.list, showForm ? { date: today } : 'skip');

  const createAnnouncementMutation = useMutation(api.coach.announcements.create);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.targetSessionId) {
      alert('Please select a session');
      return;
    }
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      await createAnnouncementMutation({
        title: formData.title,
        content: formData.content,
        targetSessionId: formData.targetSessionId as Id<"trainingSessions">,
        priority: formData.priority as "low" | "normal" | "high" | "urgent",
      });
      setShowForm(false);
      setFormData({ title: '', content: '', targetSessionId: '', priority: 'normal' });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create announcement');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-y-2">
        <div className="flex items-center gap-3">
          <Megaphone className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Announcements</h1>
            <p className="text-sm text-muted-foreground">{total ?? 0} sent</p>
          </div>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>
          <Plus className="w-4 h-4 mr-2" />
          New Announcement
        </Button>
      </div>

      {/* Create Announcement Form */}
      {showForm && (
        <div className="bg-card rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Send Message to Session Attendees</h2>
          <p className="text-sm text-muted-foreground mb-4">
            This message will be sent to parents of all players scheduled for the selected session.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Session</label>
              <select
                value={formData.targetSessionId}
                onChange={(e) => setFormData({ ...formData, targetSessionId: e.target.value })}
                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                required
              >
                <option value="">Select a session...</option>
                {sessionsData?.sessions?.map((session: any) => {
                  const type = session.sessionType
                    ? session.sessionType.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
                    : 'Session';
                  const court = session.courtLocation || null;
                  const time = session.startTime
                    ? new Date(session.startTime).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
                    : '';
                  return (
                    <option key={session.id} value={session.id}>
                      {type}{court ? ` (${court})` : ''} — {time}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="e.g., Session Rescheduled, Weather Update..."
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Message</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                rows={4}
                placeholder="Write your message..."
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Announcement'}
              </Button>
            </div>
            {submitError && <p className="text-red-500 text-sm">{submitError}</p>}
          </form>
        </div>
      )}

      {/* Announcements List */}
      <div className="space-y-4">
        {announcements.length === 0 ? (
          <div className="bg-card rounded-lg shadow p-8 text-center">
            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No announcements yet</p>
            <p className="text-sm text-muted-foreground">Send your first announcement to session attendees</p>
          </div>
        ) : (
          announcements.map((announcement) => {
            const audience = audienceLabels[announcement.targetAudience] || audienceLabels.all_members;
            const AudienceIcon = audience.icon;
            return (
              <div
                key={announcement.id}
                className={cn(
                  'bg-card rounded-lg shadow p-4 border-l-4',
                  priorityColors[announcement.priority] || priorityColors.normal
                )}
              >
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-foreground">{announcement.title}</h3>
                  <span className={cn('px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1', audience.color)}>
                    <AudienceIcon className="w-3 h-3" />
                    {audience.label}
                  </span>
                  {announcement.priority === 'urgent' && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Urgent</span>
                  )}
                  {announcement.priority === 'high' && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">High Priority</span>
                  )}
                </div>
                <p className="text-foreground whitespace-pre-wrap">{announcement.content}</p>
                <div className="mt-3 text-xs text-muted-foreground">
                  {announcement.createdByName
                    ? <span>By {announcement.createdByName} · </span>
                    : announcement.createdByEmail
                      ? <span>By {announcement.createdByEmail} · </span>
                      : null}
                  {new Date(announcement.createdAt).toLocaleDateString('en-AU', {
                    weekday: 'short', month: 'short', day: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
